import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { HashChip } from "../components/EvidenceChip";
import { ShieldCheck, AlertOctagon, Clock, FileSignature, ArrowLeft } from "lucide-react";

const STATUS_CONFIG = {
  active: { label: "Verified by KATLAS node", Icon: ShieldCheck, tone: "text-emerald-700" },
  pending: { label: "Pending KATLAS signature", Icon: Clock, tone: "text-amber-700" },
  unconfigured: { label: "KATLAS unconfigured", Icon: AlertOctagon, tone: "text-slate-600" },
  unverified: { label: "Unverified", Icon: AlertOctagon, tone: "text-amber-700" },
};

function Field({ label, value, mono }) {
  return (
    <div className="border-t border-slate-200 py-4 grid grid-cols-12 gap-4">
      <p className="col-span-12 md:col-span-4 eyebrow">{label}</p>
      <p className={`col-span-12 md:col-span-8 text-[var(--ink-heading)] break-words ${mono ? "font-mono-ui text-xs" : ""}`}>
        {value || <span className="text-[var(--ink-muted)] italic">not provided</span>}
      </p>
    </div>
  );
}

export default function PublicReceipt() {
  const { hash } = useParams();
  const [receipt, setReceipt] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.getReceipt(hash).then(setReceipt).catch((e) => setErr(e.message));
  }, [hash]);

  if (err) return <main className="max-w-3xl mx-auto px-6 py-16"><p className="text-red-700" data-testid="receipt-error">{err}</p></main>;
  if (!receipt) return <main className="max-w-3xl mx-auto px-6 py-16"><p data-testid="receipt-loading" className="text-[var(--ink-muted)]">Loading…</p></main>;

  const p = receipt.public;
  const cfg = STATUS_CONFIG[p.verification_status] || STATUS_CONFIG.unverified;
  const Icon = cfg.Icon;

  return (
    <main className="max-w-3xl mx-auto px-6 lg:px-10 py-16" data-testid="public-receipt-page">
      <Link to={`/request/${receipt.request_id}`} className="inline-flex items-center gap-1.5 text-xs text-[var(--ink-secondary)] hover:text-[var(--ink-heading)] mb-8" data-testid="back-to-request">
        <ArrowLeft size={14} /> Back to request {receipt.request_id}
      </Link>

      <header className="mb-10">
        <p className="eyebrow">Public KATLAS receipt</p>
        <h1 className="font-display text-3xl md:text-4xl text-[var(--ink-heading)] mt-3 leading-tight">
          {p.receipt_type.replace(/_/g, " ")}
        </h1>
        <div className={`flex items-center gap-2 mt-5 text-sm ${cfg.tone}`} data-testid="verification-status">
          <Icon size={16} /> {cfg.label}
          {p.katlas_signed ? (
            <span className="ml-2 text-xs text-[var(--ink-muted)] font-mono-ui">KATLAS-signed</span>
          ) : (
            <span className="ml-2 text-xs text-[var(--ink-muted)] font-mono-ui">not yet signed</span>
          )}
        </div>
      </header>

      <section className="bg-white border border-slate-200 p-7" data-testid="receipt-fields">
        <div className="flex items-center gap-2 mb-5">
          <FileSignature size={16} className="text-[var(--ink-heading)]" />
          <p className="eyebrow">Whitelisted receipt fields</p>
        </div>

        <Field label="Receipt hash" value={p.receipt_hash} mono />
        <Field label="Signer ref" value={p.signer_ref} mono />
        <Field label="Attestation ref" value={p.attestation_ref} mono />
        <Field label="Node timestamp" value={p.node_timestamp_utc} mono />
        <Field label="Event id" value={p.event_id} mono />
        <Field label="Policy id" value={p.policy_id} mono />
        <Field label="Actor role" value={p.actor_role} />
        <Field label="Counterparty role" value={p.counterparty_role} />
        <Field label="Outcome" value={p.outcome} />
        <Field label="Recorded at" value={p.timestamp_utc} mono />
        <Field label="Artifact hash" value={p.artifact_hash} mono />

        <div className="border-t border-slate-200 pt-4 mt-4">
          <p className="eyebrow mb-3">Shared evidence references</p>
          <div className="flex flex-wrap gap-2">
            {(p.shared_evidence_refs || []).map((e) => (
              <HashChip key={e.ref_id} value={e.hash} label={e.ref_id} />
            ))}
          </div>
          <p className="text-xs text-[var(--ink-mono)] mt-3 font-mono-ui">
            Withheld evidence · count {p.withheld_evidence_count}
          </p>
        </div>

        <div className="border-t border-slate-200 pt-4 mt-4">
          <p className="eyebrow mb-3">CAR summary</p>
          <ul className="space-y-2 text-sm text-[var(--ink-secondary)]">
            <li><span className="text-[var(--ink-mono)] font-mono-ui text-xs">CUSTODY · </span>{p.car_summary?.custody}</li>
            <li><span className="text-[var(--ink-mono)] font-mono-ui text-xs">AUTHORITY · </span>{p.car_summary?.authority}</li>
            <li><span className="text-[var(--ink-mono)] font-mono-ui text-xs">RECEIPTS · </span>{p.car_summary?.receipts}</li>
          </ul>
        </div>
      </section>

      <p className="text-xs text-[var(--ink-muted)] mt-6 leading-relaxed">
        This page exposes only whitelisted receipt metadata. Raw data, signed
        envelopes, bearer tokens and personal identifiers never appear here.
      </p>
    </main>
  );
}
